/**
 * OZO.direct Premium Motion & Interaction Suite
 * - Card Spotlight Glow: Dynamic cursor-following neon highlight
 * - Scroll Reveal: Silky smooth staggered viewport reveals
 * - Numeric Counters: Animated count-up for statistics and KPIs
 */

export function initSpotlight() {
  if (typeof window === "undefined") return;

  // Отслеживание курсора для эффекта неонового спотлайта на карточках
  document.addEventListener(
    "mousemove",
    (e) => {
      const card = e.target.closest(
        ".client-card, .ecosystem-card, .bento-item, .pricing-card, .dash-card, .form-wrapper, .ecosystem-banner, .feature-card"
      );

      if (card) {
        const rect = card.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        card.style.setProperty("--mouse-x", `${x}px`);
        card.style.setProperty("--mouse-y", `${y}px`);
      }
    },
    { passive: true }
  );
}

export function initScrollReveal() {
  if (typeof window === "undefined" || !("IntersectionObserver" in window))
    return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-revealed");
          observer.unobserve(entry.target);
        }
      });
    },
    {
      threshold: 0.05,
      rootMargin: "0px 0px 40px 0px",
    }
  );

  const observeElements = () => {
    const targets = document.querySelectorAll(
      ".reveal-on-scroll:not(.is-revealed), .section-header:not(.is-revealed)"
    );
    targets.forEach((el) => observer.observe(el));
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", observeElements);
  } else {
    observeElements();
  }

  // Наблюдаем за динамической вставкой лениво подгружаемых компонентов
  if ("MutationObserver" in window) {
    const mutObserver = new MutationObserver(() => {
      observeElements();
    });
    mutObserver.observe(document.body, { childList: true, subtree: true });
  }

  setTimeout(observeElements, 400);
  setTimeout(observeElements, 1200);
}

export function initCounters() {
  if (typeof window === "undefined" || !("IntersectionObserver" in window))
    return;

  const animatedSet = new WeakSet();

  const counterObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !animatedSet.has(entry.target)) {
          animatedSet.add(entry.target);
          runCounter(entry.target);
          counterObserver.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.3 }
  );

  function runCounter(el) {
    const rawText = el.getAttribute("data-counter") || el.textContent.trim();
    // Извлекаем префикс (+, -, ₸), число, и суффикс (%, x, +, дней)
    const match = rawText.match(/^([^\d]*)([\d\s.,]+)([^\d]*)$/);
    if (!match) return;

    const prefix = match[1] || "";
    const numStr = match[2].replace(/\s/g, "").replace(",", ".");
    const targetNum = parseFloat(numStr);
    const suffix = match[3] || "";
    const isDecimal = numStr.includes(".");

    if (isNaN(targetNum)) return;

    const duration = 1600;
    const startTime = performance.now();

    function update(now) {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Плавное торможение (Cubic Out)
      const ease = 1 - Math.pow(1 - progress, 3);
      const current = targetNum * ease;

      let formattedNum = "";
      if (isDecimal) {
        formattedNum = current.toFixed(1);
      } else if (targetNum >= 1000) {
        formattedNum = Math.round(current)
          .toString()
          .replace(/\B(?=(\d{3})+(?!\d))/g, " ");
      } else {
        formattedNum = Math.round(current).toString();
      }

      el.textContent = `${prefix}${formattedNum}${suffix}`;

      if (progress < 1) {
        requestAnimationFrame(update);
      } else {
        el.textContent = rawText; // Гарантируем точное финальное совпадение
      }
    }

    requestAnimationFrame(update);
  }

  const observeCounters = () => {
    const elements = document.querySelectorAll(
      ".m-val, .badge-num, .saving-num, .stat-num, [data-animate-number]"
    );
    elements.forEach((el) => {
      if (!animatedSet.has(el)) {
        if (!el.getAttribute("data-counter")) {
          el.setAttribute("data-counter", el.textContent.trim());
        }
        counterObserver.observe(el);
      }
    });
  };

  if ("MutationObserver" in window) {
    const mutObserver = new MutationObserver(() => {
      observeCounters();
    });
    mutObserver.observe(document.body, { childList: true, subtree: true });
  }

  setTimeout(observeCounters, 400);
  setTimeout(observeCounters, 1200);
}
